/**
 * Help nudge beside the help bubble (D10-UI-008, #4601).
 *
 * A small, quiet card that slides out next to the bubble now and then. For a
 * user who has never opened the chat it rotates through "Need any help?" and
 * short lines showing what the assistant really does; for a user who has, it
 * offers a "Did you know?" tip -- one of their persona's suggested questions on
 * a page they can open -- and clicking it opens the panel with that question
 * ready in the input (never sent for them). Content and frequency cap live in
 * lib/support/helpNudge.ts.
 *
 * It always reads as AI: the Lucide sparkles mark and "our AI assistant" with
 * the assistant's name from the status payload (never a literal, white-label).
 *
 * Rare by design: it waits for an idle spell on a page (no keyboard/pointer
 * activity), shows at most once a session, hides itself after a while (paused
 * on hover/focus), and keeps away for days after it is shown or closed. The
 * parent decides `active` (the suppression conditions: panel open, chat used
 * this session, a tour, a section nudge, the welcome prompt, view-as/preview,
 * offline mode); this component owns the timing and content.
 *
 * Non-blocking: no focus is taken, no overlay, and it is a polite live region
 * so a screen-reader user hears it once without losing their place.
 */
import { useEffect, useRef, useState, type RefObject } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { useEffectiveAuth } from "../../contexts/auth";
import { getVoiceStatus } from "../../api/voice";
import { browserCanRecord } from "../voice/useVoiceNote";
import { useSupportLists } from "./useSupportLists";
import {
  HELP_NUDGE_AUTO_HIDE_MS,
  HELP_NUDGE_EXIT_MS,
  canShowHelpNudge,
  helpNudgeIdleMs,
  markHelpNudgeDismissed,
  markHelpNudgeShown,
  pickHelpNudgeContent,
  readHelpNudgeRecord,
  type HelpNudgeContent,
  type HelpNudgeInputs,
} from "../../lib/support/helpNudge";

/** Any open modal dialog (welcome prompt, a form) owns the moment. */
function modalOpen(): boolean {
  return document.querySelector('[aria-modal="true"]') !== null;
}

interface Props {
  assistantName: string;
  userId: number;
  /** False whenever the nudge must not show (it hides at once if showing). */
  active: boolean;
  /** Opens the panel; a tip's question is handed over to pre-fill the input. */
  onOpen: (content: HelpNudgeContent) => void;
  /**
   * Where keyboard focus goes when the card unmounts under it (the bubble):
   * closing it with the X or Esc, or opening the panel -- whose focus return on
   * close then lands on the bubble, not on a card that no longer exists.
   */
  returnFocusRef?: RefObject<HTMLElement | null>;
  /**
   * The server's record that this user has asked the assistant something
   * before (status `has_used_chat`): counts as used, as does this browser's.
   */
  usedOnServer?: boolean;
}

export function SupportHelpNudge({
  assistantName,
  userId,
  active,
  onOpen,
  returnFocusRef,
  usedOnServer = false,
}: Props) {
  const cardRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const { role, preset } = useEffectiveAuth();
  const { allowedRoutes, allowedTours } = useSupportLists();
  const [content, setContent] = useState<HelpNudgeContent | null>(null);
  const visible = content !== null;
  const [paused, setPaused] = useState(false);
  // True for the short slide-back after an auto-hide.
  const [leaving, setLeaving] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);

  // Voice notes are mentioned only when the server has them on and this
  // browser can record (the panel's own mic rule). Shared cached status call.
  useEffect(() => {
    if (!active || !browserCanRecord()) return undefined;
    let live = true;
    void getVoiceStatus().then((s) => {
      if (live) setVoiceOn(!!s.enabled);
    });
    return () => {
      live = false;
    };
  }, [active]);

  // The content inputs, read when the timer fires (changing lists must not
  // restart the idle timer).
  const inputsRef = useRef<HelpNudgeInputs | null>(null);
  useEffect(() => {
    inputsRef.current = {
      name: assistantName,
      role,
      preset,
      allowedRoutes,
      caps: { tours: allowedTours.length > 0, voice: voiceOn },
      usedOnServer,
    };
  }, [assistantName, role, preset, allowedRoutes, allowedTours, voiceOn, usedOnServer]);

  // Hide at once when a suppression condition starts.
  useEffect(() => {
    if (!active) setContent(null);
  }, [active]);

  // Idle timer: restarts on every page change and every key/pointer press.
  useEffect(() => {
    if (!active || visible) return;
    if (!canShowHelpNudge(userId)) return;
    const delay = helpNudgeIdleMs();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const arm = () => {
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(fire, delay);
    };
    const fire = () => {
      // Not over a modal, and not in a background tab where nobody would see
      // it (that would spend the session's one showing and the days of gap).
      if (modalOpen() || document.hidden) {
        arm();
        return;
      }
      if (!canShowHelpNudge(userId)) return;
      const record = readHelpNudgeRecord(userId);
      const inputs = inputsRef.current;
      if (!record || !inputs) return;
      const next = pickHelpNudgeContent(record, inputs);
      if (!next) return;
      markHelpNudgeShown(userId, next);
      setPaused(false);
      setLeaving(false);
      setContent(next);
    };
    arm();
    // Scrolling with a wheel/trackpad is activity too (reading a long page),
    // and coming back to the tab starts a fresh idle spell.
    const events = ["keydown", "pointerdown", "wheel"] as const;
    for (const ev of events) window.addEventListener(ev, arm, { capture: true, passive: true });
    document.addEventListener("visibilitychange", arm);
    return () => {
      if (timer !== undefined) clearTimeout(timer);
      for (const ev of events) window.removeEventListener(ev, arm, { capture: true });
      document.removeEventListener("visibilitychange", arm);
    };
  }, [active, visible, userId, pathname]);

  // Auto-hide: after HELP_NUDGE_AUTO_HIDE_MS it slides back and unmounts.
  // The countdown pauses while the pointer or keyboard focus is on it.
  useEffect(() => {
    if (!visible || paused || leaving) return undefined;
    const t = setTimeout(() => setLeaving(true), HELP_NUDGE_AUTO_HIDE_MS);
    return () => clearTimeout(t);
  }, [visible, paused, leaving]);
  useEffect(() => {
    if (!leaving) return undefined;
    const t = setTimeout(() => {
      setContent(null);
      setLeaving(false);
    }, HELP_NUDGE_EXIT_MS);
    return () => clearTimeout(t);
  }, [leaving]);

  if (!content || !active) return null;

  const dismiss = () => {
    const hadFocus = cardRef.current?.contains(document.activeElement) ?? false;
    markHelpNudgeDismissed(userId);
    setContent(null);
    if (hadFocus) returnFocusRef?.current?.focus();
  };
  const open = () => {
    // Hand focus to the bubble first: the panel remembers the focused element
    // when it opens and returns focus there on close.
    returnFocusRef?.current?.focus();
    setContent(null);
    onOpen(content);
  };

  return (
    <div
      ref={cardRef}
      role="status"
      aria-live="polite"
      data-testid="support-help-nudge"
      data-nudge-kind={content.kind}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setPaused(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          dismiss();
        }
      }}
      className={`${leaving ? "crm-overlay-exit" : "crm-overlay-enter"} fixed bottom-5 right-[5.25rem] z-40 flex max-w-[calc(100vw-6.5rem)] items-center rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-lg sm:bottom-7 sm:right-[6.25rem] sm:max-w-sm`}
    >
      <button
        type="button"
        onClick={open}
        className="flex min-h-[44px] min-w-0 flex-1 items-center gap-2 rounded-l-lg py-2 pl-3 pr-1 text-left hover:bg-[var(--color-bg-page)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] transition-colors"
      >
        <Sparkles className="h-4 w-4 shrink-0 text-[var(--color-accent)]" aria-hidden="true" />
        {/* One short line (it wraps on a narrow phone): the hook, then the
            AI assistant line in a quieter tone. */}
        <span className="min-w-0 text-sm leading-snug">
          <span className="font-medium">{content.title}</span>{" "}
          <span className="text-[var(--color-text-muted)]">{content.body}</span>
        </span>
      </button>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss help suggestion"
        title="Dismiss"
        className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-r-lg text-[var(--color-text-muted)] hover:bg-[var(--color-bg-page)] hover:text-[var(--color-text-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] transition-colors"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
